import { useState, useCallback } from 'react';
import { supabase } from '../../lib/supabase';
import { BUILDINGS, GPS_RADIUS_METRES } from '../../lib/workerData';
import { formatTime } from '../../lib/utils';
import { useVisiblePolling } from '../../lib/useVisiblePolling';

const LOOKBACK_HOURS = 18;
// Operation launch date: nobody is "expected" anywhere before this. Change this
// single value to shift the launch.
//
// It has to cover assigned rentals and events as well as the roster, not just
// the roster: a week set up for testing leaves workers assigned to buildings,
// and those would otherwise show here as people who failed to turn up for a
// shift that was never real.
export const OPERATION_START_DATE = '2026-08-24';

// Local (Newfoundland) calendar date as YYYY-MM-DD. Using toISOString()
// directly would read the UTC date, which has already rolled to the next
// day for several hours every evening (e.g. all of the night shift window).
//
// Exported (along with the other helpers below) so Command Central can
// reuse this exact "who's expected / who's checked in where" logic instead
// of maintaining a second copy that could silently disagree with Today.
export function localDateStr(d = new Date()) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function buildingName(id) {
  const b = BUILDINGS.find(x => x.id === id);
  return b ? `${id} — ${b.shortName || b.name}` : id;
}

function durationLabel(signIn, signOut) {
  if (!signIn || !signOut) return '—';
  const mins = Math.max(0, Math.round((new Date(signOut) - new Date(signIn)) / 60000));
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h === 0) return `${m}m`;
  return `${h}h ${m}m`;
}

// For a worker still on site, "so far" duration against right now. Ticks
// forward each time this view reloads (every LOOKBACK reload, see the
// setInterval below) rather than every second — matches the refresh
// cadence the rest of this view already uses.
function elapsedLabel(signIn) {
  return durationLabel(signIn, new Date());
}

/** Amber marker uses check-in GPS only (gps_verified + gps_distance_metres). */
export function isGpsMismatch(row) {
  if (row.gps_verified === true) return false;
  if (row.gps_distance_metres == null) return false;
  if (isGpsVague(row)) return false;
  return row.gps_distance_metres > GPS_RADIUS_METRES;
}

/**
 * A reading whose own accuracy is wider than the building radius (cell tower
 * or wifi guess, not a real GPS fix) cannot prove anyone is off-site, so it
 * counts as unknown rather than a mismatch.
 */
function isGpsVague(row) {
  return row.gps_accuracy != null && row.gps_accuracy > GPS_RADIUS_METRES;
}

/**
 * No reading came back at all (permission denied, timeout, unsupported)
 * rather than a reading that disagreed with the building — these used to
 * render with no badge at all, which read as normal instead of unknown.
 */
export function isGpsUnknown(row) {
  return row.gps_verified !== true && (row.gps_distance_metres == null || isGpsVague(row));
}

// The arena and the youth centre keep separate standing rosters but are one
// combined building (E) for check-in, so both rosters expect workers at E.
export const ROSTER_GROUP_BUILDING = { PDIC: 'E', RPYCC: 'E' };

// Older sign-ins were recorded at F; they count as the combined building E.
export const combinedBuildingId = (id) => (id === 'F' ? 'E' : id);

/**
 * Expected workers tonight (date-aware):
 * - shift_assignments for today's day_of_week → expected at that roster's building
 * - scheduled_events / rental_schedules assigned_workers for today / current week
 * (Note: profiles.shift is deprecated; the roster table is the source of truth for E/F.)
 *
 * Returns nobody at all before OPERATION_START_DATE.
 */
export function buildExpectedMap(workers, events, rentals, roster, launched, todayDow) {
  const map = new Map(); // workerId -> { id, full_name, buildings: Set }

  if (!launched) return map;

  function ensure(w) {
    if (!map.has(w.id)) {
      map.set(w.id, { id: w.id, full_name: w.full_name || 'Worker', buildings: new Set() });
    }
    return map.get(w.id);
  }

  const workerById = Object.fromEntries(workers.map(w => [w.id, w]));

  // Standing rosters for today → expected at the building that roster covers.
  // Everyone rostered used to be expected at both E and F, because the two
  // buildings shared one roster and there was no way to tell them apart. That
  // meant someone on the arena counted as present by checking into the youth
  // centre, and vice versa.
  for (const r of roster || []) {
    const buildingId = ROSTER_GROUP_BUILDING[r.building_group];
    if (!buildingId) continue;
    const w = workerById[r.worker_id] || { id: r.worker_id, full_name: r.worker_name || 'Worker' };
    ensure(w).buildings.add(buildingId);
  }

  for (const ev of events || []) {
    for (const wid of ev.assigned_workers || []) {
      const w = workerById[wid] || { id: wid, full_name: (ev.assigned_worker_names || [])[(ev.assigned_workers || []).indexOf(wid)] || 'Worker' };
      const e = ensure(w);
      if (ev.building_id) e.buildings.add(ev.building_id);
      else if (ev.location) e.buildings.add(ev.location);
    }
  }

  for (const r of rentals || []) {
    // A rental's assigned_workers is per-week, not per-day — only count it
    // as "expected tonight" if today is actually one of the rental's
    // scheduled days. Rows without windows data fall back to "every day
    // this week" (legacy behaviour) rather than excluding everyone.
    const windows = Array.isArray(r.windows) ? r.windows : [];
    const todaysWindows = windows.filter(w => w?.day === todayDow);
    if (windows.length > 0 && todaysWindows.length === 0) continue;
    // If any of today's specific windows are pinned to a worker, that's
    // more precise than the whole-week pool (e.g. a building cleaned twice
    // a day by two different people) — use those instead of the pool.
    const pinnedToday = todaysWindows.filter(w => w?.worker_id);
    if (pinnedToday.length > 0) {
      for (const w of pinnedToday) {
        const worker = workerById[w.worker_id] || { id: w.worker_id, full_name: w.worker_name || 'Worker' };
        ensure(worker).buildings.add(r.building_id);
      }
      continue;
    }
    for (const wid of r.assigned_workers || []) {
      const w = workerById[wid] || { id: wid, full_name: 'Worker' };
      ensure(w).buildings.add(r.building_id);
    }
  }

  return map;
}

export default function TonightView() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [onSite, setOnSite] = useState([]);
  const [finished, setFinished] = useState([]);
  const [missing, setMissing] = useState([]);
  const [issues, setIssues] = useState([]);
  const [resolving, setResolving] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);

  const todayLabel = new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });

  const load = useCallback(async () => {
    setError(null);
    const since = new Date(Date.now() - LOOKBACK_HOURS * 60 * 60 * 1000).toISOString();
    const today = localDateStr();
    const todayDow = new Date().toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
    const weekStart = (() => {
      const d = new Date();
      const day = d.getDay();
      d.setDate(d.getDate() + (day === 0 ? -6 : 1 - day));
      return localDateStr(d);
    })();

    const [subsRes, workersRes, eventsRes, rentalsRes, rosterRes, issuesRes] = await Promise.all([
      supabase
        .from('checklist_submissions')
        .select('id, worker_id, worker_name, building_id, sign_in, sign_out, gps_lat, gps_lng, gps_accuracy, gps_distance_metres, gps_verified, gps_error')
        .gte('sign_in', since)
        .order('sign_in', { ascending: false }),
      supabase
        .from('profiles')
        .select('id, full_name, role, shift, assigned_buildings')
        .in('role', ['worker', 'supervisor']),
      supabase
        .from('scheduled_events')
        .select('id, building_id, location, assigned_workers, assigned_worker_names, start_date, end_date')
        .lte('start_date', today)
        .gte('end_date', today),
      supabase
        .from('rental_schedules')
        .select('id, building_id, week_start, assigned_workers, assigned_worker_names, windows')
        .eq('week_start', weekStart),
      supabase
        .from('shift_assignments')
        .select('building_group, day_of_week, shift_type, worker_id, worker_name')
        .in('building_group', Object.keys(ROSTER_GROUP_BUILDING))
        .eq('day_of_week', todayDow),
      // Not bounded to tonight's lookback window — an unresolved issue should
      // keep showing up until someone deals with it, however many days old.
      supabase
        .from('checklist_submissions')
        .select('id, worker_name, building_id, sign_in, maintenance_note')
        .eq('maintenance_flag', true)
        .eq('maintenance_resolved', false)
        .order('sign_in', { ascending: false }),
    ]);

    const firstErr = subsRes.error || workersRes.error || eventsRes.error || rentalsRes.error || rosterRes.error;
    if (firstErr) {
      console.error('Tonight view load failed:', firstErr);
      setError(firstErr.message);
      setLoading(false);
      return;
    }

    const subs = subsRes.data || [];
    const onSiteRows = subs.filter(r => !r.sign_out);
    const finishedRows = subs.filter(r => r.sign_out);
    // Which building(s) each worker actually checked into tonight — used to
    // catch a worker who signed in somewhere other than where they're
    // expected, instead of just treating any sign-in as "accounted for".
    const checkedInBuildings = new Map(); // workerId -> Set(building_id)
    for (const r of subs) {
      if (!r.worker_id) continue;
      if (!checkedInBuildings.has(r.worker_id)) checkedInBuildings.set(r.worker_id, new Set());
      checkedInBuildings.get(r.worker_id).add(combinedBuildingId(r.building_id));
    }

    const expected = buildExpectedMap(workersRes.data || [], eventsRes.data, rentalsRes.data, rosterRes.data || [], today >= OPERATION_START_DATE, todayDow);
    const missingList = [];
    for (const [, exp] of expected) {
      const expectedBuildings = [...exp.buildings];
      const checkedAt = checkedInBuildings.get(exp.id);
      // No specific building resolved (rare) — any sign-in still counts.
      // Otherwise the worker must have signed in at one of their expected buildings.
      const accountedFor = checkedAt && (expectedBuildings.length === 0 || expectedBuildings.some(b => checkedAt.has(b)));
      if (!accountedFor) {
        missingList.push({
          id: exp.id,
          full_name: exp.full_name,
          buildings: expectedBuildings,
        });
      }
    }
    missingList.sort((a, b) => a.full_name.localeCompare(b.full_name));

    // Soft-fail on its own: if maintenance_resolved hasn't been migrated on
    // this project yet, this one query errors but the rest of Tonight
    // shouldn't go down with it.
    if (issuesRes.error) {
      console.error('Tonight view: could not load reported issues:', issuesRes.error);
      setIssues([]);
    } else {
      setIssues(issuesRes.data || []);
    }

    setOnSite(onSiteRows);
    setFinished(finishedRows);
    setMissing(missingList);
    setLastRefresh(new Date());
    setLoading(false);
  }, []);

  async function resolveIssue(id) {
    setResolving(id);
    const { error: resolveErr } = await supabase
      .from('checklist_submissions')
      .update({ maintenance_resolved: true })
      .eq('id', id);
    setResolving(null);
    if (resolveErr) {
      console.error('Could not mark issue resolved:', resolveErr);
      return;
    }
    setIssues(prev => prev.filter(i => i.id !== id));
  }

  useVisiblePolling(load, 60000);

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div>
          <div style={styles.title}>Today</div>
          <div style={styles.sub}>
            {todayLabel} · Check-ins from the last {LOOKBACK_HOURS} hours · {lastRefresh ? `Updated ${formatTime(lastRefresh)}` : 'Loading…'}
          </div>
        </div>
        <button style={styles.refreshBtn} onClick={() => { setLoading(true); load(); }} disabled={loading}>
          {loading ? '…' : 'Refresh'}
        </button>
      </div>

      {error && (
        <div style={styles.error}>
          <div style={{ fontWeight: 700, marginBottom: 6 }}>Could not load tonight’s view</div>
          <div style={{ marginBottom: 8 }}>Check your internet connection, then tap Retry.</div>
          <button style={styles.retryBtn} onClick={() => { setLoading(true); load(); }}>Retry</button>
        </div>
      )}

      {loading && !lastRefresh ? (
        // Before the first fetch resolves, don't show the "all clear" /
        // "no one checked in" empty-state copy — that reads as a genuine
        // status, not a still-loading placeholder.
        <div style={styles.loadingState}>Loading tonight's status…</div>
      ) : (
        <>
          {issues.length > 0 && (
            <Section
              title="ISSUES REPORTED"
              count={issues.length}
              color="#f97316"
              empty=""
            >
              {issues.map(issue => (
                <div key={issue.id} style={{ ...styles.row, borderColor: '#854d0e', background: '#451a03' }}>
                  <div style={styles.rowTop}>
                    <div style={styles.rowName}>{buildingName(issue.building_id)}</div>
                  </div>
                  <div style={styles.rowDetail}>{issue.worker_name} · {formatTime(issue.sign_in)}</div>
                  <div style={{ color: '#fdba74', fontSize: 13, marginTop: 6, lineHeight: 1.4 }}>“{issue.maintenance_note}”</div>
                  <button
                    style={styles.resolveBtn}
                    disabled={resolving === issue.id}
                    onClick={() => resolveIssue(issue.id)}
                  >
                    {resolving === issue.id ? 'Marking resolved…' : '✓ Mark resolved'}
                  </button>
                </div>
              ))}
            </Section>
          )}

          <Section
            title="ON SITE NOW"
            count={onSite.length}
            color="#22c55e"
            empty="No one checked in"
          >
            {onSite.map(row => (
              <Row
                key={row.id}
                name={row.worker_name}
                detail={`${buildingName(row.building_id)} · in ${formatTime(row.sign_in)} · ${elapsedLabel(row.sign_in)} so far`}
                tone="green"
                gpsWarn={isGpsMismatch(row)}
                gpsGood={row.gps_verified === true}
                gpsUnknown={isGpsUnknown(row)}
                gpsDistance={row.gps_distance_metres}
                gpsError={row.gps_error}
              />
            ))}
          </Section>

          <Section
            title="FINISHED"
            count={finished.length}
            color="#94a3b8"
            empty="No completed check-outs yet"
          >
            {finished.map(row => (
              <Row
                key={row.id}
                name={row.worker_name}
                detail={`${buildingName(row.building_id)} · ${formatTime(row.sign_in)}–${formatTime(row.sign_out)} · ${durationLabel(row.sign_in, row.sign_out)}`}
                tone="grey"
                gpsWarn={isGpsMismatch(row)}
                gpsGood={row.gps_verified === true}
                gpsUnknown={isGpsUnknown(row)}
                gpsDistance={row.gps_distance_metres}
                gpsError={row.gps_error}
              />
            ))}
          </Section>

          <Section
            title="NOT CHECKED IN"
            count={missing.length}
            color="#ef4444"
            empty="Everyone expected is accounted for"
          >
            {missing.map(w => (
              <Row
                key={w.id}
                name={w.full_name}
                detail={w.buildings.length ? w.buildings.map(buildingName).join(' · ') : 'Scheduled tonight — no building set in the schedule yet'}
                tone="red"
              />
            ))}
          </Section>
        </>
      )}
    </div>
  );
}

function Section({ title, count, color, empty, children }) {
  return (
    <div style={styles.section}>
      <div style={styles.sectionHead}>
        <span style={{ ...styles.dot, background: color }} />
        <span style={{ ...styles.sectionTitle, color }}>{title}</span>
        <span style={styles.count}>{count}</span>
      </div>
      {count === 0 ? (
        <div style={styles.empty}>{empty}</div>
      ) : (
        <div style={styles.list}>{children}</div>
      )}
    </div>
  );
}

function Row({ name, detail, tone, gpsWarn, gpsGood, gpsUnknown, gpsDistance, gpsError }) {
  const border = tone === 'green' ? '#166534' : tone === 'red' ? '#7f1d1d' : '#334155';
  const bg = tone === 'green' ? '#052e16' : tone === 'red' ? '#450a0a' : '#1e293b';
  return (
    <div style={{ ...styles.row, borderColor: border, background: bg }}>
      <div style={styles.rowTop}>
        <div style={styles.rowName}>{name}</div>
      </div>
      <div style={styles.rowDetail}>{detail}</div>
      {/* Spelled out rather than left in a hover tooltip — Fabian checks
          this on his phone, where there's no hover. */}
      {gpsWarn && (
        <div style={styles.gpsWarnLine}>
          ⚠ Checked in away from the building{gpsDistance != null ? ` (about ${gpsDistance}m off)` : ''}
        </div>
      )}
      {!gpsWarn && gpsGood && (
        <div style={styles.gpsGoodLine}>✓ GPS Verified</div>
      )}
      {!gpsWarn && !gpsGood && gpsUnknown && (
        <div style={styles.gpsUnknownLine}>
          ? No GPS reading{gpsError ? ` (${gpsError})` : ''}
        </div>
      )}
    </div>
  );
}

const styles = {
  page: {
    padding: '16px 14px 40px',
    fontFamily: "'Segoe UI', system-ui, sans-serif",
  },
  loadingState: {
    padding: '40px 0',
    textAlign: 'center',
    color: '#64748b',
    fontSize: 14,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 18,
  },
  title: {
    color: '#f1f5f9',
    fontSize: 24,
    fontWeight: 800,
    letterSpacing: -0.5,
  },
  sub: {
    color: '#64748b',
    fontSize: 12,
    marginTop: 4,
  },
  refreshBtn: {
    background: '#1e293b',
    border: '1px solid #334155',
    color: '#f1f5f9',
    borderRadius: 8,
    padding: '8px 14px',
    fontWeight: 700,
    fontSize: 13,
    cursor: 'pointer',
  },
  error: {
    background: '#450a0a',
    border: '1px solid #7f1d1d',
    borderRadius: 10,
    padding: 14,
    color: '#fca5a5',
    fontSize: 13,
    marginBottom: 16,
  },
  retryBtn: {
    background: '#f97316',
    border: 'none',
    color: '#000',
    borderRadius: 6,
    padding: '7px 12px',
    fontWeight: 700,
    fontSize: 13,
    cursor: 'pointer',
  },
  section: {
    marginBottom: 22,
  },
  sectionHead: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginBottom: 10,
  },
  dot: {
    width: 8,
    height: 8,
    borderRadius: 999,
  },
  sectionTitle: {
    fontSize: 12,
    fontWeight: 800,
    letterSpacing: 0.8,
  },
  count: {
    marginLeft: 'auto',
    color: '#64748b',
    fontSize: 12,
    fontWeight: 700,
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  empty: {
    color: '#475569',
    fontSize: 13,
    padding: '10px 4px',
  },
  row: {
    border: '1px solid',
    borderRadius: 12,
    padding: '12px 14px',
  },
  rowTop: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 8,
  },
  rowName: {
    color: '#f1f5f9',
    fontWeight: 700,
    fontSize: 15,
  },
  rowDetail: {
    color: '#94a3b8',
    fontSize: 12,
    marginTop: 4,
    lineHeight: 1.4,
  },
  amber: {
    background: '#451a03',
    color: '#fbbf24',
    border: '1px solid #854d0e',
    borderRadius: 6,
    padding: '2px 8px',
    fontSize: 11,
    fontWeight: 700,
    whiteSpace: 'nowrap',
  },
  gpsWarnLine: {
    background: '#451a03',
    color: '#fbbf24',
    border: '1px solid #854d0e',
    borderRadius: 6,
    padding: '4px 8px',
    fontSize: 12,
    fontWeight: 700,
    marginTop: 6,
  },
  gpsUnknownLine: {
    background: '#1e293b',
    color: '#94a3b8',
    border: '1px solid #334155',
    borderRadius: 6,
    padding: '4px 8px',
    fontSize: 12,
    fontWeight: 700,
    marginTop: 6,
  },
  gpsGoodLine: {
    background: '#052e16',
    color: '#22c55e',
    border: '1px solid #166534',
    borderRadius: 6,
    padding: '4px 8px',
    fontSize: 12,
    fontWeight: 700,
    marginTop: 6,
  },
  resolveBtn: {
    marginTop: 10,
    background: '#1e293b',
    border: '1px solid #f97316',
    color: '#fdba74',
    borderRadius: 8,
    padding: '6px 12px',
    fontSize: 12,
    fontWeight: 700,
    cursor: 'pointer',
  },
};
